/**
 * Уровни просмотра и бюджет плотности.
 * Источник: `docs/rules/screen-levels.md`. Расходиться нельзя.
 */

import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

export type Level = 'district' | 'massif' | 'farm' | 'contour' | 'work';

/** Бюджет первого взгляда. */
export const DENSITY = {
  /** Крупных чисел (KPI) до раскрытия. */
  bigNumbers: 4,
  /** Пунктов списка до раскрытия. */
  listItems: 5,
  /** Строк таблицы до «показать все». */
  tableRows: 8,
} as const;

export interface LevelSpec {
  level: Level;
  /** Как уровень называется в цепочке: «МАССИВ», «ХОЗЯЙСТВО». */
  caption: string;
  /** В чём считается величина этого уровня. */
  denominator: string;
}

const m = defineMessages({
  districtCaption: { id: 'common.ui.levels.district.caption', defaultMessage: 'Район', description: 'Название уровня просмотра над заголовком страницы («Район → Массив → Хозяйство → Контур → Работа»); коротко, показывается капсом' },
  massifCaption: { id: 'common.ui.levels.massif.caption', defaultMessage: 'Массив', description: 'Название уровня просмотра над заголовком страницы; коротко' },
  farmCaption: { id: 'common.ui.levels.farm.caption', defaultMessage: 'Хозяйство', description: 'Название уровня просмотра над заголовком страницы; коротко' },
  contourCaption: { id: 'common.ui.levels.contour.caption', defaultMessage: 'Контур', description: 'Название уровня просмотра над заголовком страницы; коротко' },
  workCaption: { id: 'common.ui.levels.work.caption', defaultMessage: 'Работа', description: 'Название уровня просмотра над заголовком страницы; коротко' },
  districtDenominator: {
    id: 'common.ui.levels.district.denominator',
    defaultMessage: 'массивы, контуры, хозяйства и гектары района',
    description: 'В чём считаются величины на уровне района: перечень единиц счёта, продолжает фразу вроде «Считаем: …»',
  },
  massifDenominator: {
    id: 'common.ui.levels.massif.denominator',
    defaultMessage: 'контуры и хозяйства массива',
    description: 'В чём считаются величины на уровне массива: перечень единиц счёта',
  },
  farmDenominator: {
    id: 'common.ui.levels.farm.denominator',
    defaultMessage: 'контуры, обязательства и гектары хозяйства',
    description: 'В чём считаются величины на уровне хозяйства: перечень единиц счёта',
  },
  contourDenominator: {
    id: 'common.ui.levels.contour.denominator',
    defaultMessage: 'работы на контуре, части, гектары контура',
    description: 'В чём считаются величины на уровне контура: перечень единиц счёта',
  },
  workDenominator: {
    id: 'common.ui.levels.work.denominator',
    defaultMessage: 'хозяйства и работы на полях',
    description: 'В чём считаются величины на уровне работы: перечень единиц счёта',
  },
});

const SPEC = {
  district: { caption: m.districtCaption, denominator: m.districtDenominator },
  massif: { caption: m.massifCaption, denominator: m.massifDenominator },
  farm: { caption: m.farmCaption, denominator: m.farmDenominator },
  contour: { caption: m.contourCaption, denominator: m.contourDenominator },
  work: { caption: m.workCaption, denominator: m.workDenominator },
} as const;

/** Подписи уровня на языке интерфейса (собираются при вызове, не при загрузке модуля). */
export function levelSpec(level: Level): LevelSpec {
  const spec = SPEC[level];
  return { level, caption: tr(spec.caption), denominator: tr(spec.denominator) };
}
